import { useChatStore } from '@client/store';
import { Message, User, Character } from '@components'
import { Utils, memoryOptimizer, removeMessagePosition } from '@client/utility';

export interface MessageHandlers {
  handleRemoveMessage: (messageId: number) => Promise<void>;
  handleEditMessage: (
    messageId: number,
    editedMessage: string,
    type: 'user' | 'char'
  ) => Promise<void>;
  onGenerateImage: (messageId: number) => Promise<void>;
}

export const useMessageHandlers = (
  _messages: Message[],
  user: User,
  char: Character,
  chat_uuid: string,
  setIsGenerating: React.Dispatch<React.SetStateAction<boolean>>
): MessageHandlers => {
  const { setMessages, messages: storeMessages } = useChatStore();

  const handleRemoveMessage = async (messageId: number) => {
    if (!messageId) return;
    setMessages((prev) => {
      const targetIndex = prev.findIndex((msg) => msg.id === messageId);
      if (targetIndex === -1) return prev;

      const messagesToDelete = prev.slice(targetIndex);
      const idsToDelete = messagesToDelete
        .map((msg) => msg.id)
        .filter((id) => id != null);

      idsToDelete.forEach(async (id) => {
        try {
          await Utils.db.remove({ tables: 'chat_messages', match: { id } });
          await removeMessagePosition(chat_uuid, id);
        } catch (err) {
          console.error(err);
        }
      });

      const updated = prev.slice(0, targetIndex);
      memoryOptimizer.optimize();
      return updated;
    });
  };

  const handleEditMessage = async (
    messageId: number,
    editedMessage: string,
    type: 'user' | 'char'
  ) => {
    if (!messageId || !editedMessage.trim()) return;

    const targetMessage = storeMessages.find((msg) => msg.id === messageId);
    if (!targetMessage) return;

    const currentIndex = targetMessage.current ?? 0;

    const updatedAlternatives = () => {
      const arr = [...(targetMessage.alternative_messages || [])];
      while (arr.length < currentIndex) arr.push('');
      if (currentIndex > 0) arr[currentIndex - 1] = editedMessage;
      return arr;
    };

    try {
      const dbValues =
        type === 'user'
          ? { user_message: editedMessage }
          : currentIndex === 0
            ? { char_message: editedMessage }
            : { alternative_messages: updatedAlternatives() };

      const result = await Utils.db.update({
        tables: 'chat_messages',
        values: dbValues,
        match: { id: messageId },
      });

      if (!result || result.length === 0)
        throw new Error('Failed to update message in database');

      setMessages((prev) => {
        const updated = prev.map((msg) => {
          if (msg.id !== messageId || msg.type !== type) return msg;
          return currentIndex === 0
            ? { ...msg, text: editedMessage }
            : { ...msg, alternative_messages: updatedAlternatives() };
        });
        memoryOptimizer.optimize();
        return updated;
      });
    } catch (err) {
      console.error(err);
    }
  };

  const onGenerateImage = async (messageId: number) => {
    if (!messageId) return;
    try {
      const prompt = storeMessages
        .slice(-10)
        .map((msg) => `${char.persona}. ${msg.text}`)
        .join(' ');

      setIsGenerating(true);

      const res = await Utils.post<{ url: string }[]>('/api/ImageGen', {
        type: 'Anime',
        query: prompt,
      });

      const imageData = res as { url: string }[];
      if (!imageData || !imageData[0]?.url)
        throw new Error('Failed to generate image');

      const newMsg: Message = {
        id: 0,
        type: 'char',
        text: `![Generated Image](${imageData[0].url})`,
        name: char.name,
        current: 0,
        char_uuid: char.char_uuid,
      };

      const inserted = (await Utils.db.insert({
        tables: 'chat_messages',
        data: {
          char_uuid: char.char_uuid,
          chat_uuid,
          char_message: newMsg.text,
          is_image: true,
        },
      })) as { id: number }[];

      newMsg.id = inserted[0].id;
      newMsg.ref_id = inserted[0].id;
      setMessages((prev) => {
        const updated = [...prev, newMsg];
        memoryOptimizer.optimize();
        return updated;
      });
    } catch (err) {
      console.error(err);
      const errorMsg: Message = {
        id: Date.now(),
        type: 'char',
        text: '⚠️ Failed to generate image. Please try again later.',
        name: char.name,
        current: 0,
        char_uuid: char.char_uuid,
      };
      setMessages((prev) => {
        const updated = [...prev, errorMsg];
        memoryOptimizer.optimize();
        return updated;
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return { handleRemoveMessage, handleEditMessage, onGenerateImage };
};
